/**
 * Site header. The reference design has two header modes:
 *  - marketing (signed-out): Method / Pricing / Guides / Privacy + Sign in + CTA
 *  - app (signed-in demo): Report / Plan / Progress / Tools + re-scan + avatar
 * We derive the mode from the route: the "app" routes below render the app nav.
 * (Circle is intentionally omitted from this build.)
 *
 * Below 900px the inline links hide (.fx-hide-sm) and a menu button (.fx-menu-btn)
 * opens a stacked panel instead, so phones keep full navigation.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FacetMark } from './Logo';
import { C, MONO } from '../lib/theme';

const APP_ROUTES = ['/report', '/plan', '/progress', '/tools', '/account'];
const APP_NAV = [
  { to: '/report', label: 'Report' },
  { to: '/plan', label: 'Plan' },
  { to: '/progress', label: 'Progress' },
  { to: '/tools', label: 'Tools' },
  { to: '/account', label: 'Account' },
];
const MK_NAV = [
  { to: '/method', label: 'Method' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/guides', label: 'Guides' },
  { to: '/privacy', label: 'Privacy' },
];

function Wordmark() {
  return (
    <Link to="/" aria-label="Facet home" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <FacetMark size={26} variant="ink" />
      <span style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', color: C.ink }}>
        Facet
      </span>
    </Link>
  );
}

function MenuButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="fx-menu-btn"
      onClick={onClick}
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: 10,
        border: `1px solid ${C.border}`,
        background: '#fff',
        cursor: 'pointer',
        flex: 'none',
      }}
    >
      <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
        {open ? (
          <g stroke={C.ink} strokeWidth="1.6" strokeLinecap="round">
            <line x1="2" y1="2" x2="16" y2="12" />
            <line x1="16" y1="2" x2="2" y2="12" />
          </g>
        ) : (
          <g stroke={C.ink} strokeWidth="1.6" strokeLinecap="round">
            <line x1="1" y1="2" x2="17" y2="2" />
            <line x1="1" y1="7" x2="17" y2="7" />
            <line x1="1" y1="12" x2="17" y2="12" />
          </g>
        )}
      </svg>
    </button>
  );
}

export function Nav() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isApp = APP_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
  const links = isApp ? APP_NAV : MK_NAV;

  // Close the mobile panel whenever the route changes.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'rgba(250,250,247,.88)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <div
        style={{
          maxWidth: 'var(--fx-shell-max)',
          margin: '0 auto',
          padding: '0 var(--fx-gutter)',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        {isApp ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Wordmark />
              <span
                className="fx-hide-sm"
                style={{
                  fontFamily: MONO,
                  fontSize: 9,
                  letterSpacing: '0.08em',
                  color: C.accent,
                  border: `1px solid ${C.accentLight}`,
                  borderRadius: 999,
                  padding: '3px 8px',
                  background: C.accentBg,
                  whiteSpace: 'nowrap',
                }}
              >
                DEMO · @ASCEND_ALEX
              </span>
            </div>
            <nav aria-label="Primary" style={{ display: 'flex', alignItems: 'center', gap: 22, fontSize: 14 }}>
              {APP_NAV.slice(0, 4).map((n) => {
                const active = pathname === n.to;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="fx-hide-sm"
                    style={{ color: active ? C.ink : C.muted, fontWeight: active ? 600 : 400 }}
                  >
                    {n.label}
                  </Link>
                );
              })}
              <button
                onClick={() => nav('/scan')}
                className="fx-hide-sm"
                style={{
                  border: '1px solid #d8d7cf',
                  background: '#fff',
                  color: C.ink,
                  padding: '8px 14px',
                  borderRadius: 10,
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Re-scan · free
              </button>
              <Link
                to="/account"
                aria-label="Account"
                className="fx-hide-sm"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: C.accent,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 600,
                  flex: 'none',
                }}
              >
                A
              </Link>
              <MenuButton open={open} onClick={() => setOpen((v) => !v)} />
            </nav>
          </>
        ) : (
          <>
            <Wordmark />
            <nav
              aria-label="Primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 26,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {MK_NAV.map((n) => (
                <Link key={n.to} to={n.to} className="fx-hide-sm" style={{ color: C.muted }}>
                  {n.label}
                </Link>
              ))}
              <Link to="/report" className="fx-hide-sm" style={{ color: C.ink }}>
                Sign in
              </Link>
              <button
                onClick={() => nav('/scan')}
                className="fx-nav-cta"
                style={{
                  background: C.accent,
                  color: '#fff',
                  padding: '10px 18px',
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 14,
                  border: 0,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Get your baseline
              </button>
              <MenuButton open={open} onClick={() => setOpen((v) => !v)} />
            </nav>
          </>
        )}
      </div>

      {/* Mobile panel — only reachable below 900px, where the inline links hide. */}
      {open && (
        <div
          className="fx-mobile-panel"
          style={{
            borderTop: `1px solid ${C.border}`,
            background: C.bg,
            padding: '10px var(--fx-gutter) 18px',
          }}
        >
          <nav aria-label="Mobile" style={{ display: 'flex', flexDirection: 'column' }}>
            {links.map((n) => {
              const active = pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  style={{
                    padding: '13px 0',
                    fontSize: 16,
                    fontWeight: active ? 600 : 500,
                    color: active ? C.accent : C.ink,
                    borderBottom: `1px solid ${C.borderFaint}`,
                  }}
                >
                  {n.label}
                </Link>
              );
            })}
            {!isApp && (
              <Link to="/report" style={{ padding: '13px 0', fontSize: 16, fontWeight: 500, color: C.ink, borderBottom: `1px solid ${C.borderFaint}` }}>
                Sign in
              </Link>
            )}
            <button
              onClick={() => nav('/scan')}
              style={{
                marginTop: 14,
                background: C.accent,
                color: '#fff',
                padding: '13px 18px',
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 15,
                border: 0,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {isApp ? 'Re-scan · free' : 'Get your baseline'}
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
